import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useRef } from "react";

import { content } from "../../content/content";

const EASE = [0.22, 1, 0.36, 1] as const;

export function Hero() {
  const reduceMotion = useReducedMotion();
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    if (reduceMotion) {
      videoRef.current?.pause();
    }
  }, [reduceMotion]);

  return (
    <section className="relative min-h-[100svh] overflow-hidden bg-ink">
      <video
        ref={videoRef}
        data-edit-id="content.hero.videoSrc"
        className="absolute inset-0 h-full w-full object-cover"
        autoPlay={!reduceMotion}
        muted
        loop
        playsInline
        preload="auto"
        poster={content.hero.posterImage}
        aria-hidden="true"
        onCanPlay={() => window.dispatchEvent(new Event("se:hero-video-ready"))}
        onLoadedData={() =>
          window.dispatchEvent(new Event("se:hero-video-ready"))
        }
      >
        <source src={content.hero.videoSrc} type="video/mp4" />
      </video>

      <div aria-hidden="true" className="absolute inset-0 bg-ink/35" />

      <div className="relative z-10 flex min-h-[100svh] flex-col items-center justify-center px-6 text-center md:px-10">
        <motion.p
          data-edit-id="content.hero.eyebrow"
          className="mb-6 font-body text-[11px] font-medium tracking-[0.3em] text-warm-white/85 uppercase md:text-xs"
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 0.4, ease: EASE }}
          style={{ textShadow: "0 1px 12px rgba(0,0,0,0.35)" }}
        >
          {content.hero.eyebrow}
        </motion.p>

        <motion.h1
          data-edit-id="content.hero.headline"
          className="font-hero text-[clamp(2.8rem,7.4vw,6.4rem)] leading-[0.98] font-normal tracking-tight text-warm-white"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.7, ease: EASE }}
          style={{ textShadow: "0 2px 24px rgba(0,0,0,0.45)" }}
        >
          {content.hero.headline}
        </motion.h1>

        <motion.p
          data-edit-id="content.hero.tagline"
          className="mt-7 max-w-[620px] font-body text-[clamp(1rem,1.5vw,1.25rem)] leading-[1.55] text-warm-white/90"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 1.5, ease: EASE }}
          style={{ textShadow: "0 1px 16px rgba(0,0,0,0.4)" }}
        >
          {content.hero.tagline}
        </motion.p>

        <motion.div
          aria-hidden="true"
          className="mt-10 flex items-center gap-3"
          initial={{ opacity: 0, scaleX: 0 }}
          animate={{ opacity: 1, scaleX: 1 }}
          transition={{ duration: 1.4, delay: 2.2, ease: EASE }}
          style={{ transformOrigin: "center" }}
        >
          <span className="block h-px w-10 bg-saffron-light" />
          <span className="block h-1 w-1 rounded-full bg-saffron-light" />
          <span className="block h-px w-10 bg-saffron-light" />
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-2 md:bottom-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.0, delay: 3.0, ease: EASE }}
      >
        <span
          data-edit-id="content.hero.scrollLabel"
          className="font-body text-[10px] tracking-[0.3em] text-warm-white/75 uppercase"
        >
          {content.hero.scrollLabel}
        </span>
        <motion.span
          className="block h-9 w-px bg-warm-white/55"
          animate={
            reduceMotion
              ? undefined
              : { scaleY: [1, 1.6, 1], opacity: [0.4, 0.9, 0.4] }
          }
          style={{ transformOrigin: "top" }}
          transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>
    </section>
  );
}
